import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { AppState } from '../../app.state';
import { reset, setContact, setIsCompany } from '../../calculation/calculation.actions';
import { selectCalculations } from '../../calculation/calculation.state';
import { MatDialog } from '@angular/material/dialog';
import { ProgressSpinnerService } from '../../components/progress-spinner/progress-spinner.service';
import { Router } from '@angular/router';
import { BehaviorSubject, lastValueFrom, Observable } from 'rxjs';
import { createPhoneNumberValidator } from '../../common/core/validators/phone-number.validator';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { environment } from '../../../environments/environment';
import { OrdersService } from '../../../api/services/orders.service';
import { PaymentTypeEnum } from '../../../api/models/payment-type-enum';
import { LocalizationService } from 'src/app/localization/localization.service';
import { addBusinessDays } from 'src/app/common/shared/helpers/date';
import { counties } from './order.constants';
import { OfferDto } from '../../../api/models/offer-dto';
import { AuthService } from '../../auth/auth.service';
import { ShoppingCartDto } from '../../../api/models/shopping-cart-dto';
import { OffersService } from '../../../api/services/offers.service';
import { OverviewDialogComponent } from '../../common/shared/dialog/dialog.component';
import { SessionService } from '../../auth/session.service';
import { CoreComponent } from '../../core.component';
import { User } from '../../common/models/user';
import { createCNPValidator } from '../../common/core/validators/cnp.validator';

@Component({
  selector: 'order',
  templateUrl: './order.component.html',
  styleUrls: ['./order.component.scss'],
})
export class OrderComponent extends CoreComponent implements OnInit, OnDestroy {
  individualForm = this.formBuilder.group({
    cnp: new UntypedFormControl(null, [createCNPValidator()]),
    phoneNumber: new UntypedFormControl(null, [createPhoneNumberValidator()]),
    email: new UntypedFormControl(null, [Validators.email]),
    firstName: new UntypedFormControl(null, [Validators.minLength(2)]),
    lastName: new UntypedFormControl(null, [Validators.minLength(2)]),
    county: new UntypedFormControl(null),
    city: new UntypedFormControl(null),
    address: new UntypedFormControl(null),
  });
  companyForm = this.formBuilder.group({
    name: ['', Validators.required],
    registrationId: ['', Validators.required],
    registrationNumber: ['', Validators.required],
    county: ['', Validators.required],
    city: ['', Validators.required],
    address: ['', Validators.required],
    phoneNumber: new UntypedFormControl(null, [createPhoneNumberValidator()]),
    email: new UntypedFormControl(null, [Validators.email]),
    representativeFirstName: ['', Validators.required],
    representativeLastName: ['', Validators.required],
  });
  deliveryFrom = this.formBuilder.group({
    copyFromContact: new FormControl({ value: false, disabled: true }),
    address: new UntypedFormControl(null),
    county: new UntypedFormControl(null),
    city: new UntypedFormControl(null),
    phoneNumber: new UntypedFormControl(null, [createPhoneNumberValidator()]),
    toDeliverAt: new UntypedFormControl(null),
  });
  orderForm = this.formBuilder.group({
    paymentType: [PaymentTypeEnum.Card, Validators.required],
    additionalInfo: [''],
    termsConditions: [false, Validators.required],
    gdpr: [false, Validators.required],
  });
  customerType: 'fizica' | 'juridica' = 'fizica';

  offer!: OfferDto | undefined;
  counties = counties;
  filteredCounties: any[] = this.counties.slice();
  loggedInUser: Observable<User | null>;
  partialPayment = false;
  paymentTypes = PaymentTypeEnum;
  paymentType: PaymentTypeEnum = PaymentTypeEnum.Card;
  private cart!: ShoppingCartDto;
  offerSubject = new BehaviorSubject<OfferDto | null>(null);
  billingFormValid = false;

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly store: Store<AppState>,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly progressSpinnerService: ProgressSpinnerService,
    private readonly ordersService: OrdersService,
    private readonly localizationService: LocalizationService,
    private readonly authService: AuthService,
    private readonly offersService: OffersService,
    private readonly sessionService: SessionService,
  ) {
    super();
    this.loggedInUser = this.authService.currentUser;
  }

  async ngOnInit(): Promise<void> {
    this.garbageCollector.push(
      this.store.select(selectCalculations).subscribe(async (calculations) => {
        this.customerType = calculations.isCompany ? 'juridica' : 'fizica';
        this.partialPayment = calculations.partialPayment;
        if (calculations.cart?.id && calculations.cart.updatedAt !== this.cart?.updatedAt) {
          await this.getOffer(calculations.cart);
        }
        this.cart = calculations.cart as ShoppingCartDto;

        if (calculations.contact?.company) {
          this.companyForm.setValue(calculations.contact.company);
        }
        if (calculations.contact?.person) {
          this.individualForm.setValue(calculations.contact.person);
        }
        if (calculations.contact?.delivery) {
          this.deliveryFrom.setValue({ ...calculations.contact.delivery, copyFromContact: false, toDeliverAt: null });
        }
      }),
    );

    this.garbageCollector.push(
      this.individualForm.statusChanges.subscribe(() => {
        this.updateCopyFromContactState();
      }),
    );
    this.garbageCollector.push(
      this.companyForm.statusChanges.subscribe(() => {
        this.updateCopyFromContactState();
      }),
    );
  }

  updateCopyFromContactState() {
    if (this.isBillingFormValid()) {
      this.billingFormValid = true;
      this.deliveryFrom.controls.copyFromContact.enable();
    } else {
      this.billingFormValid = false;
      this.deliveryFrom.controls.copyFromContact.disable();
    }
  }

  async getOffer(cart: ShoppingCartDto): Promise<void> {
    if (!cart.calculations?.length) {
      this.offer = undefined;
      return;
    }
    this.offer = await lastValueFrom(this.offersService.offersControllerGetCartOffer({ cartId: cart.id }));
    this.offerSubject.next(this.offer);
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.store.dispatch(setIsCompany({ isCompany: this.customerType === 'juridica' }));
    this.store.dispatch(
      setContact({
        contact: {
          company: this.customerType === 'juridica' ? this.companyForm.value : undefined,
          person: this.customerType === 'fizica' ? this.individualForm.value : undefined,
          delivery: this.deliveryFrom.value,
        },
      }),
    );
  }

  async changeCustomerType() {
    this.individualForm.reset();
    this.companyForm.reset();
    if (this.deliveryFrom.controls.copyFromContact.value) {
      this.deliveryFrom.reset();
    }
    this.store.dispatch(setIsCompany({ isCompany: this.customerType === 'juridica' }));
    this.store.dispatch(setContact({ contact: {} as any }));
    if (this.partialPayment) {
      const partialAmount = this.getPartialPaymentAmount();

      if (this.offer?.companyPartialAmount !== this.offer?.partialAmount) {
        confirm(
          `Restul de plata cash maxim pentru persoane ${
            this.customerType === 'juridica' ? 'juridice e de 5000' : 'fizice e de 10000'
          } lei. Avansul se schimba la ${partialAmount.toFixed(2)} RON.`,
        );
      }
    }
  }

  async placeOrder() {
    this.progressSpinnerService.openProgressSpinner();
    const paymentType = this.orderForm.controls.paymentType.value;
    try {
      const body = await lastValueFrom(
        this.ordersService.ordersControllerCreateOrder({
          body: {
            shoppingCartId: this.cart.id,
            paymentType: this.orderForm.controls.paymentType.value,
            additionalInfo: this.orderForm.controls.additionalInfo.value,
            isPartialPayment: this.partialPayment,
            contact: {
              person: this.customerType === 'fizica' ? this.individualForm.value : undefined,
              company: this.customerType === 'juridica' ? this.companyForm.value : undefined,
              delivery: {
                ...this.deliveryFrom.value,
                toDeliverAt: this.deliveryFrom.controls.toDeliverAt.value
                  ? new Date(this.deliveryFrom.controls.toDeliverAt.value).toISOString().split('T')[0]
                  : undefined,
                copyFromContact: undefined,
              },
            },
          },
        }),
      );
      this.sessionService.removeCart();
      if (!!environment.gtm) {
        (<any>window).gtag('event', 'conversion', {
          send_to: 'AW-11371316789/jKUjCIjcoO0YELWMoq4q',
          transaction_id: body.id,
        });
      }
      if ([PaymentTypeEnum.PaymentOrder, PaymentTypeEnum.Tbi].includes(paymentType)) {
        this.progressSpinnerService.closeProgressSpinner();
        this.store.dispatch(reset());
        this.router.navigate(['order/confirmation'], { queryParams: { orderId: body.id } });
      } else {
        window.location.href = body.paymentUrl;
      }
    } catch (e) {
      this.dialog.open(OverviewDialogComponent, {
        width: '250px',
        data: {
          title: 'S-a produs o eroare',
          text: 'Ne cerem scuze, s-a produs o eroare. Va rugăm reîncercați sau contactați-ne 0740 074 206 / contact@experttabla.ro',
          buttonText: 'Reîncercare',
          buttonUrl: '../comanda',
        },
      });
    }
    this.progressSpinnerService.closeProgressSpinner();
  }

  allFormsAreValid() {
    return this.isBillingFormValid() && this.deliveryFrom.valid && this.orderForm.valid;
  }

  isBillingFormValid() {
    if (this.customerType === 'juridica' && !this.companyForm.valid) {
      return false;
    } else if (this.customerType === 'fizica' && !this.individualForm.valid) {
      return false;
    }
    return true;
  }

  copyFromContact({ checked }: MatCheckboxChange) {
    if (checked) {
      if (this.customerType === 'fizica') {
        this.deliveryFrom.controls.county.patchValue(this.individualForm.controls.county.value);
        this.deliveryFrom.controls.city.patchValue(this.individualForm.controls.city.value);
        this.deliveryFrom.controls.address.patchValue(this.individualForm.controls.address.value);
        this.deliveryFrom.controls.phoneNumber.patchValue(this.individualForm.controls.phoneNumber.value);
      } else if (this.customerType === 'juridica') {
        this.deliveryFrom.controls.county.patchValue(this.companyForm.controls.county.value);
        this.deliveryFrom.controls.city.patchValue(this.companyForm.controls.city.value);
        this.deliveryFrom.controls.address.patchValue(this.companyForm.controls.address.value);
        this.deliveryFrom.controls.phoneNumber.patchValue(this.companyForm.controls.phoneNumber.value);
      }
    }
  }

  trimWhitespaces(form: UntypedFormGroup, controlName: string) {
    if (form.controls[controlName].value) {
      form.controls[controlName].patchValue(form.controls[controlName].value.trim());
    }
  }

  filterCounties(event: any) {
    const filterValue = this.localizationService.removeDiacritics(event.target.value.toLowerCase());
    this.filteredCounties = this.counties.filter(
      (county) =>
        this.localizationService.removeDiacritics(county.label.toLowerCase()).includes(filterValue) ||
        county.value.toLowerCase() === filterValue,
    );
  }

  toDeliverAtMinDate(): Date {
    const currentDate = new Date();
    const daysToAdd = currentDate.getHours() < 12 ? 2 : 3;
    return addBusinessDays(currentDate, daysToAdd);
  }

  getPartialPaymentAmount(): number {
    if (!this.offer) {
      return 0;
    }
    return this.customerType === 'juridica' ? this.offer.companyPartialAmount : this.offer.partialAmount;
  }

  changePaymentType(paymentType: PaymentTypeEnum) {
    this.orderForm.controls.paymentType.setValue(paymentType);
    this.paymentType = paymentType;
  }
}
